import { createSupplierPerformance } from "@/apis/supplier";
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from "@/components/ui/buttons/PrimaryButton";
import CommonModal from "@/components/ui/CommonModal";
import { UserFormFields } from "@/components/UserFormFields/UserFormFields";
import { useGlobalModal } from "@/hooks/context/ModalContext";
import type {
  PerformanceEvaluationDetailItem,
  SavePerformanceEvaluationSchema,
  SrmPerformanceEvaluationRatingDtos,
} from "@/types/apis";
import { getScoreLevelByScore } from "@/utils/indicatorScore";
import { notifySuccess, notifyWarning } from "@/utils/notification";
import { useMutation } from "@tanstack/react-query";
import { Form, Tabs } from "antd";
import type { TabsProps } from "antd/lib";
import React from "react";
import { useSupplierPerformanceAppraisalModal } from "../../hooks/useSupplierPerformanceAppraisalModal";
import type {
  EvaluationDataType,
  InvitePerformanceEvaluationDetailItem,
} from "../../type";
import { EvaluationInvite } from "../EvaluationInvite";
import { PerformanceEvaluationAdvice } from "../PerformanceEvaluationAdvice";
import { PerformanceEvaluationCard } from "../PerformanceEvaluationCard";
import { PerformanceEvaluationResult } from "../PerformanceEvaluationResult";
import { SupplierEvaluationGrade } from "../PerformanceEvaluationResult/SupplierEvalutionGrade";
import styles from "./index.module.less";
import { SupplierBasicFormFields } from "./SupplierBasicFormFields";
import { useModal } from "@/hooks/modals/useModal";
import { useFlow } from "@/hooks/services/useFlow";
import { useTranslation } from "react-i18next";

// 供应商绩效考核评分表
const SupplierPerformanceAppraisalModal = () => {
  const { open, props, closeModal } = useModal(
    "PERFORMANCE_EVALUATION_NOTIFICATION",
  );
  const { t } = useTranslation("supplierPerformance");
  const { t: tCommon } = useTranslation("common");

  const { recordId: editingId, onSuccess, mode: modalMode, taskId } = props;
  const { passFlowMutation } = useFlow("COMMON_SINGLE_NODE_FLOW", false);

  const globalModal = useGlobalModal();

  const {
    form,
    evaluationData,
    onScoreChange,
    inviteEvaluationDetails,
    onInvitePersonChange,
    adviceProcessDone,
    queryResult,
    evalutionResultData,
    adviceForm,
    submitAdvice,
  } = useSupplierPerformanceAppraisalModal({
    editingId,
    appraisalModalOpen: open,
  });

  // 正在创建绩效考核评估
  const isCreating = !editingId;

  // 邀请人正在填写
  const isInviteFilling =
    modalMode === "inReview" && queryResult?.evaluationStatus === 1;

  //  评估完成
  const isEvaluationFinished =
    (modalMode === "started" && queryResult) ||
    (modalMode === "inReview" && queryResult?.evaluationStatus === 2);

  const saveEvaluationMutation = useMutation({
    mutationFn: (data: SavePerformanceEvaluationSchema) =>
      createSupplierPerformance(data),
    onSuccess() {
      notifySuccess(t("创建成功"));
      handleCloseEvaluationModal();
      onSuccess?.();
    },
  });

  const handleUpdateAdvice = async () => {
    if (!editingId) return;

    const values = await adviceForm.validateFields();
    submitAdvice(
      {
        id: editingId,
        evaluationAdvice: values.evaluationAdvice,
      },
      {
        onSuccess() {
          notifySuccess(t("填写成功"));
          handleCloseEvaluationModal();
          onSuccess?.();
        },
      },
    );
  };

  const handleEvaluate = async (
    selfDetails: PerformanceEvaluationDetailItem[],
  ) => {
    if (!editingId || !taskId) return;

    const params: SrmPerformanceEvaluationRatingDtos = selfDetails.map((d) => {
      const targetEvaluation = queryResult?.details.find(
        (e) => e.indicatorId === d.indicatorId,
      );

      return {
        detailId: targetEvaluation?.id!,
        evaluationId: targetEvaluation?.evaluationId!,
        score: d.score!,
        scoreLevel: getScoreLevelByScore(d.score)!,
      };
    });

    globalModal.confirm({
      title: t("确认操作"),
      content: t("提交后表单将无法修改，您确定要提交吗？"),
      onOk() {
        passFlowMutation.mutate(
          {
            taskId,
            flowParamsDto: {
              flowCode: "COMMON_SINGLE_NODE_FLOW",
              skipType: "PASS",
              message: t("供应商绩效考核评估通过"),
              variable: {
                ratingDtos: params,
              },
            },
          },
          {
            onSuccess() {
              notifySuccess(t("评估成功"));
              handleCloseEvaluationModal();
              onSuccess?.();
            },
          },
        );
      },
    });
  };

  const handleModalOk = async () => {
    if (!isCreating && isEvaluationFinished) {
      handleUpdateAdvice();
      return;
    }

    const values = await form.validateFields();

    // 验证自评完整性
    const selfDetails = transformToSaveDetails(evaluationData);
    if (selfDetails.length && !isSelfEvaluationCompleted(selfDetails)) {
      notifyWarning(t("请完成我的评价"));
      setActiveKey("my-evaluation");
      return;
    }

    // 评估人评估供应商绩效
    if (isInviteFilling) {
      handleEvaluate(selfDetails);
      return;
    }

    // 验证邀请评估完整性
    if (
      inviteEvaluationDetails.length &&
      !isInviteEvaluationCompleted(inviteEvaluationDetails)
    ) {
      notifyWarning(t("请选择考核人"));
      setActiveKey("invite-evaluation");
      return;
    }

    // 转换并合并数据
    const inviteDetails = transformInviteDetails(inviteEvaluationDetails);
    const allDetails = [...selfDetails, ...inviteDetails];

    globalModal.confirm({
      title: t("确认操作"),
      content: t("提交后表单将无法修改，您确定要提交吗？"),
      onOk() {
        saveEvaluationMutation.mutate({
          supplierUserId: values.supplierUserId,
          supplierCategoryId: values.supplierCategoryId,
          purchaseAmount: values.purchaseAmount,
          riskLevel: values.riskLevel,
          details: allDetails,
        });
      },
    });
  };

  const handleCloseEvaluationModal = () => {
    closeModal();
  };

  const [activeKey, setActiveKey] = React.useState("my-evaluation");

  const items: TabsProps["items"] = [
    {
      key: "my-evaluation",
      label: t("我的评分"),
      children: (
        <PerformanceEvaluationCard
          evaluationData={evaluationData}
          onScoreChange={onScoreChange}
        />
      ),
    },
    {
      key: "invite-evaluation",
      label: t("评分邀请"),
      children: (
        <EvaluationInvite
          inviteDetails={inviteEvaluationDetails}
          onInvitePersonChange={onInvitePersonChange}
        />
      ),
    },
  ];

  return (
    <>
      <CommonModal
        title={t("供应商绩效考核评分表")}
        open={open}
        onCancel={handleCloseEvaluationModal}
        width={1100}
        style={{ top: 20 }}
        footer={() => (
          <div style={{ textAlign: "center" }}>
            {adviceProcessDone ? (
              <PrimaryOutlineButton onClick={handleCloseEvaluationModal}>
                {tCommon("关闭")}
              </PrimaryOutlineButton>
            ) : (
              <PrimaryButton
                loading={saveEvaluationMutation.isPending}
                onClick={handleModalOk}
              >
                {tCommon("提交")}
              </PrimaryButton>
            )}
          </div>
        )}
      >
        <Form layout="vertical" form={form} disabled={!isCreating}>
          <UserFormFields />
          <SupplierBasicFormFields />
        </Form>

        {isInviteFilling && (
          <div className={styles.tabsWrapper}>
            <Tabs
              type="card"
              activeKey={activeKey}
              onChange={setActiveKey}
              items={[
                {
                  key: "my-evaluation",
                  label: t("我的评分"),
                  children: (
                    <PerformanceEvaluationCard
                      evaluationData={evaluationData}
                      onScoreChange={onScoreChange}
                    />
                  ),
                },
              ]}
            />
          </div>
        )}

        {isCreating && (
          <div className={styles.tabsWrapper}>
            <Tabs
              type="card"
              activeKey={activeKey}
              onChange={setActiveKey}
              items={items}
            />
          </div>
        )}

        {isEvaluationFinished && (
          <>
            <PerformanceEvaluationResult evaluationData={evalutionResultData} />
            <SupplierEvaluationGrade
              score={queryResult?.assessmentScore}
              level={queryResult?.performanceLevel}
            />
            <PerformanceEvaluationAdvice
              form={adviceForm}
              disabled={adviceProcessDone}
            />
          </>
        )}
      </CommonModal>
    </>
  );
};

// 验证自评是否完整
function isSelfEvaluationCompleted(
  details: PerformanceEvaluationDetailItem[],
): boolean {
  return details.every((d) => d.score != null && d.scoreLevel != null);
}

// 验证考核人是否已选择
function isInviteEvaluationCompleted(
  details: InvitePerformanceEvaluationDetailItem[],
): boolean {
  return details.every((d) => d.rater != null);
}

// 将评估详情转换为保存格式
function transformToSaveDetails(
  evaluationData: EvaluationDataType[],
): PerformanceEvaluationDetailItem[] {
  return evaluationData.map((item) => ({
    indicatorId: item.indicatorId,
    weight: item.weight,
    ratingDeptId: item.ratingDeptId,
    rater: item.rater,
    score: item.score,
    scoreLevel: getScoreLevelByScore(item.score),
  }));
}

// 将邀请评估详情转换为保存格式
function transformInviteDetails(
  inviteDetails: InvitePerformanceEvaluationDetailItem[],
): PerformanceEvaluationDetailItem[] {
  return inviteDetails.map((item) => {
    const { ratingDeptName, ...rest } = item;
    return { ...rest, rater: rest.rater! };
  });
}

export default SupplierPerformanceAppraisalModal;
